import React from 'react';
import { Box, Container, Flex, Heading, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
  }
};

const staggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

function JourneyStep({ year, title, description, isLast }) {
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const yearColor = useColorModeValue('gray.500', 'gray.400');
  const gradientYear = useColorModeValue(
    'linear(to-r, #200122, #6f0000)',
    'linear(to-r, #bdc3c7, white)'
  );

  return (
    <Flex>
      <Box w="1px" bg={useColorModeValue('gray.200', 'gray.700')} ml="7px" />
      <MotionBox
        variants={fadeInUpVariants}
        mb={isLast ? 0 : 12}
        ml={4}
        flex={1}
      >
        <Flex align="center" mb={2}>
          <Box
            w="15px"
            h="15px"
            borderRadius="full"
            bg={useColorModeValue('black', 'white')}
            mr={4}
            ml="-7px"
          />
          <Text
            fontWeight="bold"
            fontSize="sm"
            bgGradient={gradientYear}
            bgClip="text"
          >
            {year}
          </Text>
        </Flex>
        <Heading as="h3" size="md" mb={2} color={textColor}>
          {title}
        </Heading>
        <Text fontSize="sm" color={textColor}>
          {description}
        </Text>
      </MotionBox>
    </Flex>
  );
}

export default function Journey() {
  const journeyItems = [
    {
      year: '2020',
      title: 'Inizio come Freelance e Percorso Scolastico',
      description: 'Ho iniziato a sviluppare i miei primi siti web utilizzando JavaScript, HTML e CSS. Nel terzo anno scolastico ho studiato i fondamenti di C#, oltre alle basi di HTML e CSS.'
    },
    {
      year: '2021',
      title: 'Front-End Developer e Progressi Scolastici',
      description: 'Ho sviluppato vari siti web per il progetto PrymaCommunity, utilizzando JavaScript, HTML e CSS. Nel quarto anno scolastico ho approfondito l\'uso di JavaScript, HTML, CSS e i fondamenti di C#.'
    },
    {
      year: '2022',
      title: 'Diploma e Sviluppo ReactJS',
      description: 'Ho conseguito il diploma come Perito Informatico presso l\'I.I.S. Michele Giua. Come Front-End Developer, ho sviluppato diverse piattaforme in ReactJS, integrandole con Strapi. Durante l\'ultimo anno scolastico ho utilizzato PHP e SQL.'
    },
    {
      year: '2023',
      title: 'PrymaCommunity e Unity',
      description: 'Ho concluso il progetto PrymaCommunity, sviluppato in Java dal 2020. Mi sono occupato del marketing, della gestione dello staff, del coordinamento del progetto e dello sviluppo tecnico. Ho anche iniziato a esplorare lo sviluppo di videogiochi con Unity, creando un gioco gestionale 2D in C#.'
    },
    {
      year: '2024',
      title: 'Java Developer per Charlie Roleplay',
      description: 'Ho lanciato Charlie Roleplay, un server Minecraft "gioco di ruolo" che simula la vita reale. Ho lavorato come Java developer utilizzando le API di Paper, Spigot e Bukkit, creando oltre 30 plugin personalizzati. Ho fatto uso del reverse engineering quando necessario e ho integrato l\'intelligenza artificiale per ottimizzare il mio lavoro e apprendere rapidamente.'
    }
  ];

  const gradientTitle = useColorModeValue(
    'linear(to-r, #200122, #6f0000)',
    'linear(to-r, #bdc3c7, white)'
  );

  return (
    <Container maxW="container.md" py={16} id="journey">
      <MotionBox
        initial="hidden"
        animate="visible"
        variants={staggerVariants}
      >
        <MotionHeading
          as="h2"
          size="2xl"
          textAlign="left"
          mb={12}
          variants={fadeInUpVariants}
          bgGradient={gradientTitle}
          bgClip="text"
          display="inline-block"  // Aggiunto per evitare il taglio del testo
        >
          Il mio percorso
        </MotionHeading>
        <VStack spacing={0} align="stretch">
          {journeyItems.map((item, index) => (
            <JourneyStep
              key={index}
              year={item.year}
              title={item.title}
              description={item.description}
              isLast={index === journeyItems.length - 1}
            />
          ))}
        </VStack>
      </MotionBox>
    </Container>
  );
}